/* eslint-disable no-shadow */

import { ActionTree, GetterTree, MutationTree, Module } from 'vuex';
import { setupApollo } from '../../../lib/apollo_setup';
import { FETCH_ACCOUNT_DATA } from '@/graphql/account/queries';
import { Account, AccountData } from '../../../models/account';
import { rolesArray } from '@/lib/jwt';

export interface AccountState {
  accountData: AccountData | null; // Store raw data
  error: string | null;
}

interface AccountLocation {
  cityname: string;
  stateabbr: string;
  latitude: number;
  longitude: number;
  x: number;
  y: number;
}

const apolloProvider = setupApollo();

const state: AccountState = {
  accountData: null,
  error: null,
};

const getters: GetterTree<AccountState, any> = {
  isAuthenticated: (state): boolean => !!state.accountData,
  getAccount: (state): Account | null => {
    if (!state.accountData) return null;
    return new Account({
      ...state.accountData,
    });
  },
  getZipCode: (_, getters): string | null => getters.getAccount?.location?.zipcode || null,
  getLocation: (_, getters): AccountLocation | null => {
    const location = getters.getAccount?.location;
    if (location) {
      return {
        cityname: location.cityname,
        stateabbr: location.stateabbr,
        latitude: location.latitude,
        longitude: location.longitude,
        y: location.latitude,
        x: location.longitude,
      };
    }
    return null;
  },
};

const mutations: MutationTree<AccountState> = {
  setAccountData(state, accountData: AccountData) {
    state.accountData = accountData;
  },
  setError(state, error: string) {
    state.error = error;
  },
};

const actions: ActionTree<AccountState, any> = {
  async fetchAccountData({ commit }) {
    try {
      const result = await apolloProvider.defaultClient.query({
        query: FETCH_ACCOUNT_DATA,
        fetchPolicy: 'network-only',
      });

      if (result.errors?.length) {
        throw new Error(result.errors.map((e) => e.message).join(', '));
      }

      const accountData = result.data.viewer;
      // ensure accountData is not null or undefined
      if (!accountData) return;

      commit('setAccountData', {
        ssoUUID: accountData.id,
        id: accountData.id,
        firstName: accountData.firstName,
        lastName: accountData.lastName,
        email: accountData.email,
        originalPhoto: accountData.photoUrl,
        croppedPhoto: accountData.photoUrl,
        location: {
          latitude: parseFloat(accountData.location.geoPoint.y),
          longitude: parseFloat(accountData.location.geoPoint.x),
          zipcode: accountData.location.zipcode,
          cityname: accountData.location.cityname,
          stateabbr: accountData.location.stateabbr,
        },
        hasPremiumHistory: accountData.hasPremiumHistory,
        profilePhotoStatus: accountData.profilePhotoStatus,
        createdAt: accountData.createdAt,
        roles: rolesArray(),
      } as AccountData);
    } catch (error: any) {
      console.error('Error fetching account data:', error);

      const errorMessage = error?.message || 'Failed to fetch account data.';
      commit('setError', errorMessage);
    }
  },
};

const accountModule: Module<AccountState, any> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default accountModule;
